import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  FieldContext,
  IDictionaryFieldFieldsFragment,
  IGetDictionaryFieldsQuery,
  IGetDictionaryFieldsQueryVariables,
  ManagedEntityType,
  PermissionKey,
} from '../../../utils/apollo/graphqlSchema';
import { useDebounce } from '../../../utils/hooks';
import usePermissionSchemas from '../../PermissionControls/hooks/usePermissionSchemas';
import FieldsTable from './FieldsTable';
import useApplyTags from '../useApplyTags';
import { IDictionaryFieldWithRowExpand } from './types';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { useDictionarySearchParamsEffect } from '../../../utils/searchParamsDataSharing';
import fieldsQuery from '../../Formulas/apollo/GET_DICTIONARY_FIELDS';
import { RowSelectionState } from '@tanstack/react-table';
import { freeze } from '@reduxjs/toolkit';
import { mapSubRows } from './utils';
import { ContentLoader } from '../../../components/Loaders';
import { DictionaryTypeEnum } from '../../../utils/interfaces';
import DictionaryNoResults from '../DictionaryNoResults';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { CategoryEntityType } from '../../../components/Categories/utils';
import { FIELD_REMOVE_CATEGORY } from '../../../utils/apollo/fields/queries';
import { FIELD_ADD_CATEGORY } from '../../../utils/apollo/fields/queries';
import { fieldInformationUpdated } from '../../../utils/analytics/events/field/events';
import DictionaryHeader from '../utils/DictionaryHeader';
import { useGlobalApplyTags } from '../utils/useGlobalApplyTags';

const fallbackData = freeze<IDictionaryFieldWithRowExpand[]>([]);

const Fields = () => {
  const { track } = useAnalytics();
  useEffect(() => {
    track(
      pageViewed({
        category: 'dictionary',
        name: 'field',
        id: null,
      }),
    );
  }, [track]);

  const { selectedSchemaId, permittedSchemaIds } = usePermissionSchemas({
    requestedPermissionKey: PermissionKey.FieldRead,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // populate schemaId + search term from search params data sharing
  useDictionarySearchParamsEffect(setSearchQuery);

  const { checkIsPermitted } = usePermission();
  const isPermittedToAssignCategory = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.CategoryCreate,
      PermissionKey.FieldEdit,
    ],
    schemaId: selectedSchemaId,
  });

  const { data, loading, refetch } = useQuery<
    IGetDictionaryFieldsQuery,
    IGetDictionaryFieldsQueryVariables
  >(fieldsQuery, {
    variables: {
      term: debouncedSearchQuery,
      schemaId: selectedSchemaId,
      includeFields: [],
      func: null,
      context: FieldContext.Filter,
      events: [],
    },
  });

  const allFields = data?.fields ?? fallbackData;
  const fields = useMemo(() => mapSubRows(allFields), [allFields]);

  const handleSubmitNewTags = (entities: IDictionaryFieldFieldsFragment[]) => {
    entities.forEach((field) => track(fieldInformationUpdated(field)));
    refetch();
    setRowSelection({});
  };

  const handleApplyTags = useApplyTags({
    entityType: CategoryEntityType.Field,
    addCategoryMutation: FIELD_ADD_CATEGORY,
    removeCategoryMutation: FIELD_REMOVE_CATEGORY,
    managedEntityType: ManagedEntityType.Field,
    getEntityId: (entity: IDictionaryFieldFieldsFragment) => entity.key,
    getEntityCategories: (entity: IDictionaryFieldFieldsFragment) =>
      entity.categories,
    onSuccess: handleSubmitNewTags,
  });

  const handleGlobalApplyTags = useGlobalApplyTags({
    rowSelection,
    entities: allFields,
    selectedSchemaId,
    handleApplyTags,
    getEntityId: (entity) => entity.key,
  });

  const selectedEntityCategories = fields
    .filter((field) => rowSelection[field.key])
    .map((field) => ({ categories: field.categories }));

  return (
    <>
      <DictionaryHeader
        selectedSchemaId={selectedSchemaId}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        title="Field"
        dictionaryItemCount={fields.length}
        permittedSchemaIds={permittedSchemaIds}
        onApplyTags={handleGlobalApplyTags}
        isPermittedToAssignCategory={isPermittedToAssignCategory}
        showApplyTags={Object.keys(rowSelection).length > 0}
        selectedEntities={selectedEntityCategories}
      />
      {loading ? (
        <ContentLoader />
      ) : fields.length === 0 ? (
        <DictionaryNoResults entityType={DictionaryTypeEnum.DictionaryField} />
      ) : (
        <FieldsTable
          fields={fields}
          schemaId={selectedSchemaId}
          onApplyTags={handleApplyTags}
          onSetFavorite={refetch}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          isPermittedToAssignCategory={isPermittedToAssignCategory}
        />
      )}
    </>
  );
};

export default Fields;
